import {useMemo} from "react"
import {get} from "lodash"
import {OperationVariables, QueryHookOptions, QueryResult, useQuery} from "@apollo/client"
import API from "@irati/utils/services/API/utils/errorHandler/register"
import {ApolloLoad, deriveHookNameAndRenameFunctionForDevtools, DocumentNode, PBApolloError, ServiceOptions} from "./helpers/hookShared"

/**
 * Helper type to construct derived types from the return type of a useQuery hook.
 * @param A - The type of the original hook, you can use `typeof` to convert JavaScript into the type world
 * @param B - The new values to extend into this type, you must at least override `data` with some new derived value
 *
 * @example
 * const useBudgetsQuery = createUseQuery<BudgetCategories, QueryBudgetCategoriesArgs>()
 * type UseBudgetDerived = UseQueryExtend<typeof useBudgetsQuery, { data: SomeNewData }>
 *
 * function myNewHook(): UseBudgetDerived {
 *   const {data, ...rest} = useBudgetsQuery()
 *   return { ...rest, data: {} as SomeNewData }
 * }
 */
export type UseQueryExtend<A extends (...x: any[]) => any, B extends {data: any}> = Prettify<
  Omit<ReturnType<A>, "data"> & WithPartial<B, "data">
>

export type UseQueryResult<Data, Variables extends OperationVariables = Record<string, never>> = Prettify<
  Pick<QueryResult<Data, Variables>, "loading" | "networkStatus" | "called" | "refetch" | "variables" | "fetchMore" | "updateQuery"> & {
    /** Data from the request */
    data: Data | undefined
    /**
     * An error object from the failing service
     * @deprecated please use `errorMsg`
     */
    error?: PBApolloError
    errorMsg?: string
    /** A pending promise that can be passed to `<Loading />` components or collected in array.
     *
     * @example
     * function MyComp() {
     *   const {load, data} = useBudgetsList()
     *
     *   return <Loading load={load}>{data}</Loading>
     * }
     *
     * @example
     * function MyComp() {
     *   const {load: budgetLoad, data} = useBudgetsList()
     *   const {load: rewardsLoad} = useScheduledRewardsQuery()
     *
     *   return <Loading load={[budgetLoad, rewardsLoad]}>{data}</Loading>
     * }
     */
    load: ApolloLoad
  }
>

/**
 * Stripped down variables to pass to useQuery
 * More can be added if identified as needed, but minimsing the api surface for now
 */
type UseQueryOptions<A, B extends OperationVariables> = Prettify<
  Pick<QueryHookOptions<A, B>, "skip" | "fetchPolicy" | "pollInterval" | "notifyOnNetworkStatusChange" | "refetchWritePolicy">
>

/**
 * Create a query hook for reuse throughout the codebase. This is highly recomended over call `useQuery` directly.
 *
 * The benifit of using this factory function is to get strongly typed values, and marshal the output into more commonly used formats,
 * not limited to but including a `load` value for passing to `Loading` components
 *
 * For details of the underlying `useQuery` see [apollo client](https://www.apollographql.com/docs/react/data/queries)
 *
 * @example
 * // create our reusable hook
 * export const useBudgetsQuery = createUseQuery<BudgetCategories, QueryBudgetCategoriesArgs>(
 *   gql`query WalletBudgets($userId: String!) {
 *       budgetCategories(userId: $userId) {
 *         categories {
 *           uuid
 *         }
 *       }
 *     }`,
 *   {
 *     key: "budgetCategories",
 *     errorHandlers: [budgetErrorHandler]
 *   }
 * )
 *
 * // somewhere else
 * const MyComp = () => {
 *   const {loading, error, data} = useBudgetsQuery({ userId: 'blah' })
 * }
 */
export function createUseQuery<A, B extends OperationVariables = Record<string, never>>(
  query: DocumentNode,
  {key, errorHandlers = [], ...useQueryOptions}: Prettify<ServiceOptions & UseQueryOptions<A, B>>
) {
  errorHandlers.forEach((handler) => {
    API.add(handler)
  })

  function useHook(variables: B, options: UseQueryOptions<A, B> = {}): UseQueryResult<A, B> {
    const apollo = useQuery<A, B>(query, {
      variables,
      context: useQueryOptions.forceEU ? {reqEUinstance: true} : {},
      ...useQueryOptions,
      ...options
    })

    return useMemo(() => {
      // here we assert the key index will be of type `A` - don't assume it should be
      // `keyof A` because we often define types in terms of the nested graphql type, not the root type
      const data = key && apollo.data ? (get(apollo.data as any, key) as A) : apollo.data

      // Here we are overriding the error type to be a PB Error as we know this will be handled by our client
      const error = apollo.error as PBApolloError | undefined

      return {
        ...apollo,
        error,
        errorMsg: error?.networkError?.formattedMessage ?? error?.message,
        load: {
          ...apollo,
          error,
          __brand: "ApolloLoad"
        },
        data
      }
    }, [apollo])
  }

  deriveHookNameAndRenameFunctionForDevtools(useHook, "Query", query)
  return useHook
}
